import React, { useState, useEffect, SyntheticEvent, useCallback } from "react";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { Typography, Box } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import {
    GridRowsProp,
    GridRowModesModel,
    GridRowModes,
    DataGridPro,
    GridColumns,
    GridRowParams,
    MuiEvent,
    GridToolbarContainer,
    GridActionsCellItem,
    GridEventListener,
    GridRowId,
    GridRowModel,
    GridRenderCellParams,
    GridEnrichedColDef,
    useGridApiContext,
    GridColDef,
    GridColumnHeaderParams,
    GridSortModel,
    gridVisibleRowCountSelector,
    gridVisibleColumnDefinitionsSelector,
    gridVisibleSortedRowIdsSelector,
    useGridApiRef,
    ValueOptions,
} from "@mui/x-data-grid-pro";
import { randomId } from "@mui/x-data-grid-generator";
import { IColumn, ISetupDetails, IDropdownItems, IButton } from "./interfaces";
import { toLowerCaseObject } from "../../utils/to-lowercase-object";
import { formatDate, getHour } from "../../utils/time";
import { truncate } from "../../utils/text";
import { BASE_URL, OCP_APIM_SUBSCRIPTION_KEY, getAxios, route } from "../../utils/endpoints"
import { DropdownValue } from "../../types/DropdownValue";

export const useDatagridProPagination = () => {
    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(25);
    const [orderBy, setOrderBy] = useState<GridSortModel | undefined>(undefined);

    const onPageChange = (newPage: number) => {
        setPage(newPage);
    }

    const onPageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
    }

    const onChangeOrderBy = (newSortModel: GridSortModel) => {
        setOrderBy(newSortModel);
    }

    return {
        page,
        pageSize,
        orderBy,
        onPageChange,
        onPageSizeChange,
        onChangeOrderBy
    }
}

export const useCustomDatagridPro = (
    tableName: string,
    page: number,
    pageSize: number,
    orderBy: string,
) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);

    //columns from setup details
    const [sCols, setScols] = useState<IColumn[]>([]);

    //datagrid data
    const [columns, setColumns] = useState<GridColumns>([]);
    const [rows, setRows] = useState<GridRowsProp>([]);
    const [totalRecordCount, setTotalRecordCount] = useState<number>(0);

    //filters
    const [filter, setFilter] = useState<string>("");
    const [search, setSearch] = useState<string>("");
    const [isSearchEnabled, setIsSearchEnabled] = useState<boolean>(false);
    const [isFilterEnabled, setIsFilterEnabled] = useState<boolean>(false);

    //buttons
    const [buttons, setButtons] = useState<IButton[]>([]); 

    //dropdown items
    const [dropdownItems, setDropdownItems] = useState<IDropdownItems[]>([]);

    //row manipulation
    const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
    const [processMode, setProcessMode] = React.useState<"edit" | "create" | null>(null);

    //primary keys
    const [primaryKeys, setPrimaryKeys] = useState<Record<string, any>>({});
    const [primaryKeyStr, setPrimaryKeyStr] = useState<string>("");

    const getDatagridType = (type: string) => {
        switch (type) {
            case "varchar": {
                return "string";
            }

            case "int": {
                return "number";
            }

            case "datetime": {
                return "dateTime";
            }
        }
    }

    const onRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };

    const handleRowEditStart: GridEventListener<"rowEditStart"> = (params: GridRowParams, event: MuiEvent<SyntheticEvent>) => {
        event.defaultMuiPrevented = true;
    }

    const handleRowEditStop: GridEventListener<"rowEditStart"> = (params: GridRowParams, event: MuiEvent<SyntheticEvent>) => {
        event.defaultMuiPrevented = true;
    }

    const processRowUpdate = async (newRow: GridRowModel) => {
        const updatedRow = { ...newRow, isNew: false };

        if (processMode === "edit") {
            await onUpdateRow(updatedRow);
        }

        else {
            await onSaveRow(newRow);
        }

        return updatedRow;
    };

    const onHandleAddClick = () => {
        let keys: string = "";
        let newRecord: Record<string, any> = {};

        const sortedColumns = sCols.sort((a, b) => a.order > b.order ? 1 : -1);

        sortedColumns.forEach((sCol, index) => {
            if(sCol.isPrimaryKey) {
                keys += sCol.name + "," + " ";
            }

            newRecord[sCol.name.toLocaleLowerCase()] = "";
        });

        newRecord.id = ""

        setRows((prevState) => [...prevState, { ...newRecord, isNew: true }]);

        setProcessMode("create");

        setPrimaryKeyStr(keys.substring(0, keys.length - 2));

        setRowModesModel((prevState) => ({
            ...prevState,
            [newRecord.id]: { mode: GridRowModes.Edit }
        }));
    }

    const onHandleEditClick = (id: GridRowId, row: any) => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
        
        setProcessMode("edit");
    }

    const onHandleSaveClick = (id: GridRowId) => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    }

    const onHandleCloseClick = (id: GridRowId) => {
        setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        });

        const editedRow = rows.find((row) => row.id === id);

        if (editedRow!.isNew) {
            setRows(rows.filter((row) => row.id !== id));
        }
    }

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value);
    }

    const onChangeSelect = (event: SelectChangeEvent<unknown>) => {
        setFilter(event.target.value as string);
    }

    const convertColumnsToGridColumns = useCallback((columns: IColumn[]) => {
        const cols: GridColumns = columns.map((column) => {
            const opts: ValueOptions[] | null = column.dropdownItems && column.dropdownItems.map((item) => {
                return {
                    label: item.description,
                    value: item.id
                }
            })


            return {
                field: column.name.toLowerCase(),
                headerName: column.heading,
                width: 300,
                type: column.isDropdown ? "singleSelect" : getDatagridType(column.type),
                valueOptions: opts ? opts : [],
                headerAlign: "center",
                align: "center",
                resizable: false,
                editable: !column.readOnly,
                renderHeader: (params) => {
                    return (
                        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                            {params.colDef.headerName!}
                        </Typography>
                    )
                },
                renderCell: (params) => {
                    const value: string = params.value ? String(params.value) : "n/a"

                    return (
                        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                            {value}
                        </Typography>
                    )
                }
            }
        });

        const editCol: GridEnrichedColDef = {
            field: "actions",
            type: "actions",
            headerName: "Actions",
            width: 100,
            cellClassName: "actions",
            getActions(params) {
                const isInEditMode: boolean = rowModesModel[params.id]?.mode === GridRowModes.Edit;

                if (isInEditMode) {
                    return [
                        <GridActionsCellItem
                            icon={<SaveIcon />}
                            label="Save"
                            sx={{
                                color: 'primary.main',
                            }}
                            onClick={() => onHandleSaveClick(params.id)}
                        />,
                        <GridActionsCellItem
                            icon={<CancelIcon />}
                            label="Cancel"
                            className="textPrimary"
                            onClick={() => onHandleCloseClick(params.id)}
                            color="inherit"
                        />,
                    ]
                }

                return [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label="Edit"
                        className="textPrimary"
                        onClick={() => onHandleEditClick(params.id, params.row)}
                        color="inherit"
                    />
                ]
            },
        };

        setColumns([...cols, editCol]);
    }, [rowModesModel, primaryKeys]);

    const onSetupDetails = useCallback(async () => {
        try {
            const response = await fetch(`${BASE_URL}/${route.apiTable}/${tableName}/setup-details?filter=&search=`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${sessionStorage.getItem("token")}`,
                    "Ocp-Apim-Subscription-Key": OCP_APIM_SUBSCRIPTION_KEY
                }
            });

            const setupDetails: ISetupDetails = await response.json();

            setIsFilterEnabled(setupDetails.dropdownEnabled);
            setIsSearchEnabled(setupDetails.searchEnabled);

            if (setupDetails.dropdownItems) {
                setDropdownItems(setupDetails.dropdownItems);

                setFilter(setupDetails.dropdownItems[0].id as string);
            }

            if (setupDetails.buttons) {
                setButtons(setupDetails.buttons);
            }

            if (setupDetails.columns) {
                const cols = setupDetails.columns.sort((a, b) => a.order - b.order)
                .filter((column) => column.visible);

                setScols(cols);
                convertColumnsToGridColumns(cols);
            }
        }

        catch (error) {
            console.error(error)
        }
    }, [tableName]);

    const getRows = async () => {
        setIsLoading(true);

        try {
            console.log("search: ", search);

            const response = await getAxios(`${route.apiTable}/${tableName}/values`, "GET", undefined, {
                filter: filter,
                search: search,
                orderBy: orderBy,
                startIndex: (pageSize * page),
                pageSize: pageSize
            });

            const data = await response.data;

            if (Array.isArray(data)) {
                const lowerCaseRows: object[] = data.map((row: any) => {
                    return toLowerCaseObject(row);
                })
    
                setRows(lowerCaseRows);
                setTotalRecordCount(data[0]._overall_count_);
            } else alert("This is not an array");
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    const onUpdateRow = async (updatedRow: GridRowModel) => {
        const sortedColumns: IColumn[] = sCols.sort((a, b) => a.order > b.order ? 1 : -1);

        let pKeys: Record<string, any> = {};

        type ObjectKey = keyof typeof updatedRow;

        let counter = 0;

        sortedColumns.forEach((column, index) => {
            if (column.isPrimaryKey) {
                counter++;
                pKeys[`_pkfield${counter}`] = updatedRow[column.name.toLocaleLowerCase() as ObjectKey];
            }
        });

        try {
            const Values: Record<string, any> = {
                ...updatedRow,
                ...pKeys
            };

            const bodyJSON = JSON.stringify({
                TrailerPrimaryKey: null,
                Filter: null,
                Values
            })

            const response = await fetch(`${BASE_URL}/${route.apiTable}/${tableName}/values`, {
                method: "PUT",
                headers: {
                    Authorization: `Bearer ${sessionStorage.getItem("token")}`,
                    "Ocp-Apim-Subscription-Key": OCP_APIM_SUBSCRIPTION_KEY
                },
                body: bodyJSON,
            });

        } catch (error) {
            alert(error);
            console.error(error);
        }
    }

    const onSaveRow = async (newRow: GridRowModel) => {
        try {
            const Values: Record<string, any> = {
                ...newRow,
            }

            const bodyJSON = JSON.stringify({
                Filter: filter,
                Values,
                primaryKeys: primaryKeyStr
            });

            const response = await fetch(`${BASE_URL}/${route.apiTable}/${tableName}/values`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${sessionStorage.getItem("token")}`,
                    "Ocp-Apim-Subscription-Key": OCP_APIM_SUBSCRIPTION_KEY
                },
                body: bodyJSON
            });

        } catch (error) {
            console.error(error)
        }
    }

    useEffect(() => {
        onSetupDetails();
    }, [onSetupDetails]);

    useEffect(() => {
        getRows();
    }, [tableName, page, pageSize, orderBy, filter])

    useEffect(() => {
        convertColumnsToGridColumns(sCols);
    }, [convertColumnsToGridColumns]);

    return {
        isLoading,
        isFilterEnabled,
        isSearchEnabled,
        columns,
        rows,
        totalRecordCount,
        rowModesModel,
        buttons,
        dropdownItems,
        filter,
        search,
        getRows,
        handleRowEditStart,
        onChange,
        onChangeSelect,
        handleRowEditStop,
        onRowModesModelChange,
        processRowUpdate,
        onHandleAddClick
    }
}